/* eslint-disable react/jsx-props-no-spreading */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import UserAvatar from 'components/global/UserAvatar';
import BottomBar from '../BottomBar';
import TextArea from '../form/TextArea';
import FileUpload from '../../global/FileUpload';
import { IMAGE_FILE_TYPES } from '../../global/acceptedFileTypes';
import { updateUser } from '../../../actions/user';
import { updateProviderBio } from '../../../actions/preferences';
import usePersistedDraft from '../hooks/usePersistedDraft';

const MatchingPreferences = ({
  openErrorDialog,
  openLoadingPanel,
  closeLoadingPanel,
  previousPage,
  nextPage,
  errorCallback,
  setPage,
  clinicYN,
  clinicCounsellorProfile,
  currentSubStep,
  skipToEnd,
  specialized,
  draftValues,
  onDraftChange,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [selectedAvatar, setSelectedAvatar] = useState(
    draftValues?.avatar || null,
  );
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: draftValues || {
      avatar: null,
      overview: '',
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const fileSizeError = () =>
    openErrorDialog(
      intl.formatMessage({
        defaultMessage:
          'There was a problem with the file you selected. Please check to make sure your file meets the max size limit',
      }),
    );

  const renderAvatar = () => {
    if (!selectedAvatar) return null;

    return (
      <UserAvatar
        src={avatarPreviewUrl}
        size={200}
        style={{ marginBottom: '20px', marginRight: 0, display: 'flex' }}
      />
    );
  };

  const onSubmit = async values => {
    const errorMessage = intl.formatMessage({
      defaultMessage: 'There was a problem updating your matching preferences.',
    });

    const userValues = new FormData();
    if (values.avatar) userValues.append('avatar', values.avatar[0]);

    const providerBioValues = { provider_bio: { overview: values.overview } };

    openLoadingPanel(
      intl.formatMessage({
        defaultMessage: 'Updating matching preferences...',
      }),
    );

    try {
      await Promise.all([
        dispatch(updateUser(userValues)),
        dispatch(updateProviderBio(providerBioValues)),
      ]);
      closeLoadingPanel();

      if (clinicYN === 'true' || clinicCounsellorProfile) {
        setPage(8);
      } else {
        nextPage();
      }
    } catch (err) {
      errorCallback(err, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title" style={{ marginBottom: '20px' }}>
        <h1>
          {intl.formatMessage({ defaultMessage: 'Matching Preferences' })}
        </h1>
      </div>
      <div style={{ marginBottom: '40px' }}>
        <h4>
          {intl.formatMessage({
            defaultMessage:
              'If you are interested in accessing new clients through GreenShield Health, we will need a photo and a profile description. Otherwise, skip this screen',
          })}
        </h4>
      </div>
      <Controller
        name="avatar"
        control={control}
        rules={{ required: intl.formatMessage({ defaultMessage: 'Required' }) }}
        render={({ field }) => (
          <FileUpload
            {...field}
            accept={IMAGE_FILE_TYPES}
            label={intl.formatMessage({
              defaultMessage:
                'Please upload a headshot that is friendly yet professional:',
            })}
            onDropAccepted={files => {
              field.onChange(files);
              setSelectedAvatar(files);
              if (files && files[0]) {
                setAvatarPreviewUrl(URL.createObjectURL(files[0]));
              }
            }}
            onDropRejected={fileSizeError}
            error={errors.avatar?.message}
          />
        )}
      />
      {renderAvatar()}
      <h3>
        {intl.formatMessage({
          defaultMessage:
            'Please describe your practice and give clients a sense of the benefits they will get from therapy with you:',
        })}
      </h3>
      <div className="input-row">
        <Controller
          name="overview"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field }) => (
            <TextArea {...field} error={errors.overview?.message} />
          )}
        />
      </div>
      <BottomBar
        openLoadingPanel={openLoadingPanel}
        closeLoadingPanel={closeLoadingPanel}
        nextPage={nextPage}
        previousPage={previousPage}
        errorCallback={errorCallback}
        currentSubStep={currentSubStep}
        skipToEnd={skipToEnd}
        specialized={specialized}
      />
    </form>
  );
};

MatchingPreferences.propTypes = {
  openErrorDialog: PropTypes.func.isRequired,
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  clinicYN: PropTypes.string.isRequired,
  clinicCounsellorProfile: PropTypes.object,
  currentSubStep: PropTypes.number.isRequired,
  skipToEnd: PropTypes.func,
  specialized: PropTypes.bool,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default MatchingPreferences;
